'use client';

import { useEffectEvent, useLayoutEffect } from '@react-aria/utils';
import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { DismissButton, useOverlay } from 'react-aria';
import { Link as RACLink } from 'react-aria-components';
import { matchesBreakpoint, useBreakpoint } from '../../utils/breakpoints.js';
import { stringLikeChildrenToString } from '../../utils/string-like-children-to-string.js';
import { useIsMobile } from '../../utils/use-is-mobile.js';
import { AppShellContext } from '../app-shell/app-shell-context.js';
import { Tooltip } from '../tooltip/tooltip.js';
import type { Href, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

const SideNavContext = createContext<{ isOpen: boolean }>({ isOpen: false });

export interface UNSTABLE_SideNavProps {
  children: ReactNode;
  /**
   * Whether the side nav is open. Only needed when the side nav is not controlled by the app shell.
   */
  isOpen?: boolean;
  /**
   * Callback function that is called when the side nav is opened or closed.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether the side nav should be expanded by default. Only affects large viewports.
   * @default true
   */
  defaultIsOpen?: boolean;
}

export function UNSTABLE_SideNav({ children, isOpen, defaultIsOpen = true, onOpenChange }: UNSTABLE_SideNavProps) {
  const appShellContext = useContext(AppShellContext);
  const [isHydrated, setIsHydrated] = useState(false);
  const resolvedIsOpen = isOpen ?? (isHydrated ? appShellContext?.sideNavIsOpen : defaultIsOpen);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const isOverlaySideNav = useBreakpoint({ max: 'md' });

  function handleClose() {
    appShellContext?.toggleSideNav(false);
    onOpenChange?.(false);
  }

  const { overlayProps, underlayProps } = useOverlay(
    {
      isDismissable: isOverlaySideNav,
      isOpen: resolvedIsOpen,
      onClose: () => handleClose(),
    },
    sideNavRef,
  );

  const resetState = useEffectEvent(() => {
    setIsHydrated(true);

    appShellContext?.toggleSideNav(defaultIsOpen && matchesBreakpoint({ min: 'lg' }));
  });

  // Hydrate state and reset when screen size changes to or from overlay mode
  useLayoutEffect(() => {
    resetState();
  }, [isOverlaySideNav]);

  return (
    <SideNavContext.Provider value={{ isOpen: resolvedIsOpen ?? false }}>
      {isMobile && (
        <div
          {...underlayProps}
          className="cim-side-nav-backdrop"
          aria-hidden={!resolvedIsOpen}
          data-expanded={resolvedIsOpen}
          data-hydrated={isHydrated || undefined}
        />
      )}
      {isMobile && resolvedIsOpen && <DismissButton onDismiss={() => handleClose()} />}
      <div
        {...(isOverlaySideNav ? overlayProps : {})}
        className="cim-side-nav"
        data-expanded={resolvedIsOpen}
        data-hydrated={isHydrated || undefined}
        aria-label="Side navigation"
        ref={sideNavRef}
        // Make the side nav rail inert when hidden on mobile devices to prevent user interaction and assistive technology focus
        // Workaround for React not having type definitions for the `inert` attribute
        // eslint-disable-next-line internal/no-extra-props-in-spread
        {...{ inert: isMobile && !resolvedIsOpen ? '' : undefined }}
      >
        {children}
      </div>
    </SideNavContext.Provider>
  );
}

export interface UNSTABLE_SideNavListProps {
  children: ReactNode;
}

export function UNSTABLE_SideNavList({ children }: UNSTABLE_SideNavListProps) {
  return <nav className="cim-side-nav-list">{children}</nav>;
}

export interface UNSTABLE_SideNavListItemProps {
  children: StringLikeChildren;
  href: Href;
  isActive?: boolean;
  icon?: ReactNode;
}

export function UNSTABLE_SideNavListItem({ children, href, isActive, icon }: UNSTABLE_SideNavListItemProps) {
  const { isOpen } = useContext(SideNavContext);

  return (
    <Tooltip label={stringLikeChildrenToString(children)} placement="right" isDisabled={isOpen}>
      <RACLink href={href} className="cim-side-nav-list-item" data-active={isActive}>
        {icon}
        <Text as="span" variant="medium" UNSAFE_className="cim-side-nav-list-item-label">
          {children}
        </Text>
      </RACLink>
    </Tooltip>
  );
}

export interface UNSTABLE_SideNavListSectionProps {
  children: ReactNode;
  label?: string;
}

export function UNSTABLE_SideNavListSection({ children, label }: UNSTABLE_SideNavListSectionProps) {
  return (
    <div className="cim-side-nav-list-section">
      {label && (
        <div className="cim-side-nav-list-section-header">
          <Text as="p" variant="small-semibold" tone="subtle" UNSAFE_className="cim-side-nav-list-section-header-label">
            {label}
          </Text>
        </div>
      )}
      {children}
    </div>
  );
}
